import { Box, Card, Stack, Typography } from '@mui/material';

import type { CfiRating } from '~/data/hooks/useCfi';

import { ratingLabels } from './ratingLabels';

type Counts = Record<CfiRating, number>;

type Props = {
  counts: Counts;
  /**
   * Drop the card's bottom gutter because `CfiPublishBar` is tucked directly
   * under it, so the tiles and the two buttons read as one block. The viewer has
   * no bar to attach and keeps the gutter.
   */
  flush?: boolean;
};

// Tile is transparent; only the count number carries the rating color.
const ratingNumberColor: Record<CfiRating, string> = {
  favorable: 'success.main',
  neutral: 'info.main',
  unfavorable: 'error.main',
  not_applicable: 'text.secondary',
};

const ORDER: CfiRating[] = ['favorable', 'neutral', 'unfavorable', 'not_applicable'];

export const CfiSummaryDashboard = ({ counts, flush = false }: Props) => {
  const total = ORDER.reduce((sum, r) => sum + (counts[r] ?? 0), 0);

  return (
    <Card variant="outlined" sx={{ p: 2, mb: flush ? 0 : 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 1.5 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          Findings summary
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {total} {total === 1 ? 'finding' : 'findings'}
        </Typography>
      </Stack>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: 'repeat(2, 1fr)',
            sm: 'repeat(4, 1fr)',
          },
          gap: 1.5,
        }}
      >
        {ORDER.map((rating) => (
          <Box key={rating} sx={{ p: 1.5 }}>
            <Typography
              variant="h5"
              sx={{ fontWeight: 700, lineHeight: 1.2, color: ratingNumberColor[rating] }}
            >
              {counts[rating] ?? 0}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              {ratingLabels[rating]}
            </Typography>
          </Box>
        ))}
      </Box>
    </Card>
  );
};
